import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { YStack, XStack, Text, Spinner } from "tamagui";
import { RiFileCopyLine } from "react-icons/ri";

import SkinV2Screen from "@components/layout/SkinV2Screen";
import { appToast } from "@components/Toast";
import { v2 } from "@screen/Home/pages/GasShop/skin";
import { colors } from "@styles/colors";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { buildXContextHeader } from "@utils/api/headers";
import { IdentityContext } from "@utils/store/user/useUser";

import SectionLabel from "./components/ui/SectionLabel";
import ListContainer from "./components/ui/ListContainer";
import InfoNote from "./components/ui/InfoNote";
import {
  getBill,
  type BillDetail,
} from "./API/handleBillsAPI";
import Code39Barcode from "./bills/Code39Barcode";
import BillDocumentCard from "./bills/BillDocumentCard";
import BillPaymentsCard from "./bills/BillPaymentsCard";
import {
  billPeriodLabel,
  billStatusMeta,
  billTypeLabel,
  cvsUnavailableText,
  groupDigits,
  money,
  ymdSlash,
} from "./bills/billDisplay";

const tabular = { fontVariantNumeric: "tabular-nums" as const };

const copyText = (value: string, label: string) => {
  navigator.clipboard?.writeText(value);
  appToast.success(`已複製${label}`);
};

/** 卡內一列：左標籤、右值 */
/** 繳費金額的橘色：colors.amber（#F59E0B）在白底對比只有約 2:1，改用深一階（18px 粗體屬大字，對比 ≥ 3:1） */
const AMOUNT_ORANGE = "#EA580C";

const Line = ({
  label,
  value,
  strong,
  valueColor,
}: {
  label: string;
  value: string;
  strong?: boolean;
  valueColor?: string;
}) => (
  <XStack jc="space-between" ai="baseline" gap="$3">
    <Text fontSize={strong ? 14 : 13} fontWeight={strong ? "800" : "400"} color={strong ? v2.title : v2.body}>
      {label}
    </Text>
    <Text
      fontSize={strong ? 18 : 13}
      fontWeight={strong ? "800" : "600"}
      color={valueColor ?? v2.title}
      style={tabular}>
      {value}
    </Text>
  </XStack>
);

const Card = ({ children }: { children: React.ReactNode }) => (
  <ListContainer px="$3.5" py="$3.5" gap="$2.5">
    {children}
  </ListContainer>
);

/**
 * 卡片內的分段切換（繳費方式）。不用頁面層級的 SkinTabs 底線分頁：放在卡片裡會像整頁在切換。
 * 淺灰底軌＋選中的白色膠囊，等寬撐滿，手指好點。
 */
const MethodSegment = ({
  options,
  value,
  onChange,
}: {
  options: { key: string; label: string }[];
  value: string;
  onChange: (key: string) => void;
}) => (
  <XStack bg={v2.chipBg} br={999} p={3} role="tablist">
    {options.map((o) => {
      const active = o.key === value;
      return (
        <XStack
          key={o.key}
          f={1}
          ai="center"
          jc="center"
          py={7}
          br={999}
          bg={active ? v2.card : "transparent"}
          cursor="pointer"
          role="tab"
          aria-selected={active}
          pressStyle={{ opacity: 0.75 }}
          style={active ? { boxShadow: "0 1px 4px rgba(15, 23, 42, 0.12)" } : undefined}
          onPress={() => onChange(o.key)}>
          <Text fontSize={13} fontWeight={active ? "700" : "500"} color={active ? v2.primary : v2.chipText}>
            {o.label}
          </Text>
        </XStack>
      );
    })}
  </XStack>
);

/** 一種繳費方式的內容（標題由分頁籤負責） */
const MethodSection = ({ children }: { children: React.ReactNode }) => <YStack gap="$2">{children}</YStack>;

/** 標籤＋大字號碼（銀行代碼、帳號）；帶 copyLabel 時右邊有「複製」 */
const BigNumber = ({
  label,
  value,
  suffix,
  copyLabel,
}: {
  label: string;
  value: string;
  suffix?: string;
  copyLabel?: string;
}) => (
  <YStack gap="$1">
    <Text fontSize={12} color={v2.sub}>
      {label}
    </Text>
    <XStack ai="center" jc="space-between" gap="$2">
      <XStack ai="baseline" gap="$1.5" f={1} flexWrap="wrap">
        <Text fontSize={20} fontWeight="800" color={v2.title} fontFamily="monospace" style={tabular}>
          {value}
        </Text>
        {suffix && (
          <Text fontSize={13} fontWeight="700" color={v2.title}>
            {suffix}
          </Text>
        )}
      </XStack>
      {copyLabel && (
        <XStack
          ai="center"
          gap="$1"
          px="$2.5"
          py="$1.5"
          br={999}
          bg={v2.primarySoft}
          cursor="pointer"
          pressStyle={{ opacity: 0.7 }}
          onPress={() => copyText(value.replace(/\s/g, ""), copyLabel)}>
          <RiFileCopyLine size={13} color={v2.primary} />
          <Text fontSize={12} fontWeight="700" color={v2.primary}>
            複製
          </Text>
        </XStack>
      )}
    </XStack>
  </YStack>
);

/**
 * 繳費資訊：期限、金額，下面用分段切換繳費方式（同一層級，一次看一種）：
 * - 轉帳／ATM：王道銀行虛擬帳號（平台代收，金額綁「這張帳單未繳」，所以可能跟應付總計不同）
 * - 匯款：瓦斯行自己的帳戶（帳單設定 show_bank 打開才有）
 * - 超商代收：三段條碼
 * 只有一種方式時不顯示切換。預設選第一種（虛擬帳號會自動銷帳，排最前面）。
 */
const PaymentCard = ({ bill }: { bill: BillDetail }) => {
  const codes = bill.payment_codes;
  const bank = bill.bank;
  const cvsText = codes ? cvsUnavailableText(codes.cvs_unavailable_reason) : null;
  const amount = codes ? codes.amount : bill.totals.amount_due;

  const methods: { key: string; label: string; content: React.ReactNode }[] = [];
  if (codes?.virtual_account) {
    methods.push({
      key: "atm",
      label: "轉帳／ATM",
      content: (
        <MethodSection>
          {/* 轉帳要先輸入銀行代碼才能填帳號，代碼跟帳號一樣醒目 */}
          <BigNumber label="銀行代碼" value="048" suffix="王道銀行" />
          <BigNumber label="虛擬帳號" value={groupDigits(codes.virtual_account)} copyLabel="虛擬帳號" />
          <Text fontSize={11} color={v2.sub}>
            請轉入上列繳費金額，金額不符無法入帳；此帳號僅限這張帳單使用。
          </Text>
        </MethodSection>
      ),
    });
  }
  if (bank) {
    methods.push({
      key: "bank",
      label: "匯款",
      content: (
        <MethodSection>
          {bank.bank_name && <BigNumber label="銀行" value={bank.bank_name} />}
          {bank.account_name && (
            <YStack gap="$1">
              <Text fontSize={12} color={v2.sub}>
                戶名
              </Text>
              <Text fontSize={14} fontWeight="700" color={v2.title}>
                {bank.account_name}
              </Text>
            </YStack>
          )}
          <BigNumber label="帳號" value={bank.account_number} copyLabel="帳號" />
          <Text fontSize={11} color={v2.sub}>
            匯款至瓦斯行帳戶；匯款後請告知瓦斯行，方便對帳。
          </Text>
        </MethodSection>
      ),
    });
  }
  if (codes && (codes.cvs_barcode || cvsText)) {
    methods.push({
      key: "cvs",
      label: "超商",
      content: (
        <MethodSection>
          {codes.cvs_barcode ? (
            <>
              <YStack gap="$3" py="$2" px="$1" bg="#fff">
                <Code39Barcode value={codes.cvs_barcode.barcode1} />
                <Code39Barcode value={codes.cvs_barcode.barcode2} />
                <Code39Barcode value={codes.cvs_barcode.barcode3} />
              </YStack>
              <Text fontSize={11} color={v2.sub} ta="center">
                到 7-ELEVEN／全家／萊爾富／OK 櫃檯出示條碼繳費；掃不到時請把螢幕調亮。
              </Text>
            </>
          ) : (
            <Text fontSize={12} color={v2.body}>
              {cvsText}
            </Text>
          )}
        </MethodSection>
      ),
    });
  }

  const [tab, setTab] = useState<string>(methods[0]?.key ?? "");
  const active = methods.find((m) => m.key === tab) ?? methods[0];

  return (
    <YStack>
      <SectionLabel title="繳費資訊" />
      <Card>
        {codes && <Line label="繳費期限" value={ymdSlash(codes.payment_deadline)} />}
        <Line label="繳費金額" value={money(amount)} strong valueColor={AMOUNT_ORANGE} />
        {codes && codes.amount !== bill.totals.amount_due && (
          <Text fontSize={11} color={v2.sub}>
            繳費金額只含本期帳單；上期欠款或另計費用請直接與瓦斯行結清。
          </Text>
        )}
        {active && (
          <YStack gap="$3" pt="$3" btw={1} btc={v2.divider}>
            {methods.length > 1 ? (
              <MethodSegment options={methods.map(({ key, label }) => ({ key, label }))} value={active.key} onChange={setTab} />
            ) : (
              <Text fontSize={13} fontWeight="700" color={v2.title}>
                {active.label}
              </Text>
            )}
            {active.content}
          </YStack>
        )}
      </Card>
    </YStack>
  );
};

/**
 * 帳單明細（GET /app-api/bills/:id，需 X-Context）。
 * 上面是客戶要動手的部分（繳費資訊、匯款帳戶），下面是帳單本體（BillDocumentCard，口徑同紙本帳單）。
 * 繳費資訊排在最前面：客戶點進來最常是為了繳錢。
 */
export const AccountBillDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { contextId, billId } = useParams();
  const { cusJwtToken } = useCusAuth();

  const ctx = (location.state as { context?: IdentityContext } | null)?.context ?? null;
  const xContext = buildXContextHeader(ctx) ?? (contextId ? `context:${contextId}` : "");

  const [bill, setBill] = useState<BillDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!cusJwtToken || !xContext || !billId) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    getBill({ jwtToken: cusJwtToken, xContext, billId })
      .then((res) => {
        if (!active) return;
        if (res.state === "success") setBill(res.data);
        else console.log("bill detail err", res.error);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [cusJwtToken, xContext, billId]);

  const back = () => navigate(`/account/detail/${contextId}/bills`, { state: { context: ctx } });

  const status = bill ? billStatusMeta(bill) : null;
  const subHeader = bill && status && (
    <YStack mb={16} gap={4}>
      <Text color={v2.hazeSub} fontSize={13}>
        {bill.supplier.name ?? "瓦斯行"} · {billPeriodLabel(bill.period_start, bill.period_end)}
      </Text>
      <XStack ai="center" gap={8}>
        <Text color={v2.hazeInk} fontSize={22} fontWeight="800" style={tabular}>
          {money(bill.totals.amount_due)}
        </Text>
        <Text fontSize={11} fontWeight="700" color={status.fg} bg={status.bg} px={6} py={1} br={999}>
          {status.label}
        </Text>
      </XStack>
    </YStack>
  );

  const hasPhone = !!bill?.supplier.phone;

  return (
    <SkinV2Screen
      title={bill ? billTypeLabel(bill.bill_type) : "帳單"}
      onBack={back}
      subHeader={subHeader}
      band="short">
      <YStack className="gas-stagger-kids" gap="$3.5">
        {loading ? (
          <YStack f={1} ai="center" jc="center" py="$8">
            <Spinner size="large" color={colors.primary} />
          </YStack>
        ) : !bill ? (
          <YStack f={1} ai="center" jc="center" py="$8">
            <Text fontSize={14} color={v2.body}>
              找不到這張帳單
            </Text>
          </YStack>
        ) : (
          <>
            {bill.is_overdue && (
              <InfoNote tone="danger">
                這張帳單已超過繳費期限（{ymdSlash(bill.payment_deadline)}），請盡快繳費或聯絡瓦斯行。
              </InfoNote>
            )}

            {bill.status !== "paid" && (bill.payment_codes || bill.bank) && <PaymentCard bill={bill} />}
            {bill.status !== "paid" && !bill.payment_codes && !bill.bank && (
              <InfoNote tone="primary">
                請依瓦斯行告知的方式繳費{hasPhone ? `，有疑問請來電 ${bill.supplier.phone}` : ""}。
              </InfoNote>
            )}

            {/* 帳單本體：明細以下排成一張紙本帳單 */}
            <BillDocumentCard bill={bill} />
            <BillPaymentsCard payments={bill.payments} />
          </>
        )}
      </YStack>
    </SkinV2Screen>
  );
};

export default AccountBillDetail;
